import React, { useEffect } from 'react';
import { FieldArrayWithId, UseFormReturn, useWatch } from 'react-hook-form';
import { UseTrackedFieldArrayRemove } from '../../../../forms/use-tracked-field-array';
import { EventPanelFormType, EventPanelOptions } from '@mark43/cad-api';
import { useOpenEventPanel } from '../../../use-open-event-panel';
import { useAllowAddingTowVehicles } from '../../../use-allow-adding-tow-vehicles';
import { useIntegratedActionBroadcaster } from '../../../../action-broadcaster/use-integrated-action-broadcaster';
import { EventNotificationsEntityIdProvider } from '../../../../event-notifications/event-notifications-entity-id-context';
import { HStack, IconButton, Text, FormRow, DataList, DataItem } from '@cad-arc';
import { FormSelect } from '../../../../forms/integrated-form-controls/form-select';
import { FormCheckbox } from '../../../../forms/integrated-form-controls/form-checkbox';
import { some } from 'lodash';
import { EventPanelVehicleBaseKey } from '../types';
import { FormNumberInput } from '../../../../forms/integrated-form-controls/form-number-input';
import { useApplicationType } from '../../../../common/hooks/use-application-type';
import { EntityPanelFormVehiclesFieldset } from '../../../../panels/entity-panels/forms/vehicles/entity-panel-vehicles-fieldset';
import { FormInputButton } from '../../../../forms/integrated-form-components/form-button';
import { v4 } from 'uuid';
import { getInitialValuesForTowVehicle } from './event-panel-vehicle-helpers';
import { useEventMetadataStore } from '../../event-metadata';
import { useIsClearedEvent } from '../../use-is-cleared-event';
import { EventPanelFormOriAliasSelect } from '../../event-panel-form-ori-alias-select';
import { getUniversalSearchLabel } from '../../../../common/helpers/get-usx-labels';
import { useEnabledFeatures } from '../../../../common/hooks/enabled-features';

function _EventPanelFormVehicleFieldset(props: {
    form: UseFormReturn<EventPanelFormType>;
    index: number;
    field: FieldArrayWithId<EventPanelFormType, 'vehicles', 'rhfKey'>;
    remove: UseTrackedFieldArrayRemove;
    eventPanelOptions: EventPanelOptions;
    allowMembersControl?: boolean;
    displayAsCards?: boolean;
    baseKey: EventPanelVehicleBaseKey;
    eventId?: number | null;
    isReadOnly?: boolean;
    userManuallyAddedField?: boolean;
}): React.ReactElement {
    const {
        eventId,
        form: eventForm,
        index,
        field,
        eventPanelOptions,
        remove,
        baseKey,
        isReadOnly,
        allowMembersControl,
        displayAsCards,
        userManuallyAddedField,
    } = props;
    const isNewEvent = !eventId;
    const openEventPanel = useOpenEventPanel();
    const { isFirstResponder } = useApplicationType();

    const isPersisted = useWatch({
        control: eventForm.control,
        name: `${baseKey}.${index}.isPersisted`,
    });

    const vehicleId = useWatch({
        control: eventForm.control,
        name: `${baseKey}.${index}.id`,
        // disable useWatch in new event form. 'vehicleId' is always undefined in new event panel.
        // if we watch 'vehicleId' in new event panel, there will be 'Can't perform a React state update on an unmounted component' warning,
        // since 'useWatch' is called after 'setValue' in 'onReplaceVehicles'
        disabled: !eventId,
    });

    const eventLocation = useWatch({
        control: eventForm.control,
        name: 'location',
    });

    const stateOptionValue = useWatch({
        control: eventForm.control,
        name: `${baseKey}.${index}.stateOption.value`,
    });

    const queryInDex = useWatch({
        control: eventForm.control,
        name: `${baseKey}.${index}.queryInDex`,
    });

    const showInvolvementTypeOther = useWatch({
        control: eventForm.control,
        name: `${props.baseKey}.${index}.involvementTypeOption`,
    })?.isOther;

    const showDexFields = !props.displayAsCards || !isPersisted;
    const allowAddingTowVehicles = useAllowAddingTowVehicles();
    const { trigger } = useIntegratedActionBroadcaster();
    const showTowVehicleButton =
        allowAddingTowVehicles && eventLocation && stateOptionValue && isPersisted && !isReadOnly;

    const eventNumber = useEventMetadataStore().state?.metadata?.agencyEventNumber;
    const { dexNextEnabled } = useEnabledFeatures();
    const isClearedEvent = useIsClearedEvent();

    useEffect(
        function resetInvolvementTypeOther() {
            if (!showInvolvementTypeOther) {
                eventForm.setValue(`${baseKey}.${index}.involvementTypeOther`, null);
            }
        },
        [eventForm, index, baseKey, showInvolvementTypeOther]
    );
    useEffect(
        function dontQuerySavedEntity() {
            if (isPersisted) {
                // Don't query people that are saved already
                eventForm.setValue(`${baseKey}.${index}.queryInDex`, false);
            }
        },
        [baseKey, eventForm, index, isPersisted]
    );

    const dexQueryFields = useWatch({
        control: eventForm.control,
        name: [`${baseKey}.${index}.plateNumber`, `${baseKey}.${index}.vinNumber`],
    });

    useEffect(
        function enableQueryInDex() {
            if ((isNewEvent || !isPersisted) && !eventForm.formState.isSubmitting) {
                const someFieldsHaveValues = some(dexQueryFields, (f) => !!f);

                if (someFieldsHaveValues) {
                    eventForm.setValue(`${baseKey}.${index}.queryInDex`, true);
                }
            }
        },
        [dexQueryFields, index, isNewEvent, baseKey, isPersisted, eventForm]
    );

    return (
        <EventNotificationsEntityIdProvider entityId={vehicleId ?? undefined}>
            {field.eventInfo?.isChildEvent && baseKey === 'mergedVehicles' && !isFirstResponder ? (
                <DataList variant="stacked">
                    <DataItem dataKey="Merged Event">
                        <HStack>
                            <Text>{field.eventInfo.agencyEventNumber}</Text>
                            <IconButton
                                size="sm"
                                variant="ghost"
                                icon="OpenNewWindow"
                                aria-label="open event"
                                onClick={() => {
                                    const eventId = field.eventInfo?.id;
                                    if (eventId) {
                                        openEventPanel({
                                            eventId: field.eventInfo?.id ?? undefined,
                                        });
                                    }
                                }}
                            />
                        </HStack>
                    </DataItem>
                </DataList>
            ) : undefined}

            <EntityPanelFormVehiclesFieldset
                index={index}
                remove={remove}
                entityPanelOptions={eventPanelOptions}
                allowMembersControl={allowMembersControl}
                displayAsCards={displayAsCards}
                baseKey={baseKey}
                isNewEntity={!eventId}
                isReadOnly={isReadOnly}
                entityNumber={eventNumber}
                eventId={eventId ?? undefined}
                userManuallyAddedField={userManuallyAddedField}
            />
            <>
                {showDexFields && (
                    <FormRow>
                        <FormCheckbox
                            label={`Query in ${getUniversalSearchLabel(dexNextEnabled)}`}
                            name={`${baseKey}.${index}.queryInDex`}
                            control={props.form.control}
                            asEditable={false}
                            length={isFirstResponder ? undefined : 'sm'}
                            size="sm"
                            isDisabled={isClearedEvent}
                            checkboxProps={
                                isClearedEvent ? { sx: { cursor: 'not-allowed' } } : undefined
                            }
                        />
                        {queryInDex && (
                            <>
                                {eventPanelOptions.purposeCodeOptions.length !== 0 && (
                                    <FormSelect
                                        length={isFirstResponder ? 'md' : 'sm'}
                                        control={eventForm.control}
                                        label="Purpose Code"
                                        name={`${baseKey}.${index}.purposeCode`}
                                        asEditable={false}
                                        options={eventPanelOptions.purposeCodeOptions}
                                    />
                                )}
                                {eventPanelOptions.plateTypeOptions.length !== 0 && (
                                    <>
                                        <FormSelect
                                            length={isFirstResponder ? 'md' : 'sm'}
                                            control={eventForm.control}
                                            label="Plate Type"
                                            name={`${baseKey}.${index}.plateType`}
                                            asEditable={false}
                                            options={eventPanelOptions.plateTypeOptions}
                                        />
                                        <FormNumberInput
                                            length={isFirstResponder ? 'md' : 'sm'}
                                            control={eventForm.control}
                                            label="Plate Year"
                                            name={`${baseKey}.${index}.plateYear`}
                                            asEditable={false}
                                        />
                                        <EventPanelFormOriAliasSelect
                                            isFirstResponder={isFirstResponder}
                                            isPersisted={isPersisted}
                                        />
                                    </>
                                )}
                                <EventPanelFormOriAliasSelect
                                    isFirstResponder={isFirstResponder}
                                    isPersisted={isPersisted}
                                />
                            </>
                        )}
                    </FormRow>
                )}

                {showTowVehicleButton && (
                    <HStack justifyContent="flex-end">
                        <FormInputButton
                            length="md"
                            style={{ alignSelf: 'center' }}
                            onClick={() => {
                                if (eventId) {
                                    trigger([
                                        {
                                            targetWindowType: 'EVENT_V2',
                                            id: v4(),
                                            type: 'ADD_TOW_VEHICLE_TO_EVENT_FORM',
                                            payload: {
                                                eventId,
                                                initialState: getInitialValuesForTowVehicle(
                                                    eventForm,
                                                    baseKey,
                                                    index
                                                ),
                                            },
                                        },
                                    ]);
                                }
                            }}
                            variant="ghost"
                        >
                            Tow Vehicle
                        </FormInputButton>
                    </HStack>
                )}
            </>
        </EventNotificationsEntityIdProvider>
    );
}

export const EventPanelFormVehicleFieldset = React.memo(
    _EventPanelFormVehicleFieldset,
    (prevProps, nextProps) => {
        return (
            prevProps.eventPanelOptions === nextProps.eventPanelOptions &&
            prevProps.remove === nextProps.remove &&
            prevProps.index === nextProps.index &&
            prevProps.form === nextProps.form
        );
    }
);
